import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { authenticatedApi } from "@/lib/api";

/**
 * Client-side mirror of the server prompt-registry item definitions served by
 * GET /api/analysis-catalog/:version. Matches the legacy `AlwaysListItemDef` /
 * `CounselingSkillItemDef` shapes from `@/lib/prompt-registry`.
 */
export interface AlwaysListItemDef {
  id: string;
  label: string;
  appliesTo: "initial" | "follow-up" | "both";
}

export interface CounselingSkillItemDef {
  id: string;
  label: string;
}

export interface AnalysisCatalog {
  alwaysList: AlwaysListItemDef[];
  counselingSkills: CounselingSkillItemDef[];
}

const EMPTY_CATALOG: AnalysisCatalog = { alwaysList: [], counselingSkills: [] };

export interface AnalysisCatalogState {
  /**
   * Null only while the catalog is still loading. Callers gate rendering on
   * this so labels resolve before first paint — the async equivalent of the
   * legacy synchronous registry lookup (no raw-id label flash).
   */
  catalog: AnalysisCatalog | null;
}

/**
 * Fetches the registry-backed item catalog for a prompt version. On fetch
 * failure (or a missing prompt version) resolves to an empty catalog so the
 * scorecards fall back to the raw item id, matching legacy registry semantics.
 */
export function useAnalysisCatalog(promptVersion: string): AnalysisCatalogState {
  const { getToken } = useAuth();
  const query = useQuery({
    queryKey: ["analysis-catalog", promptVersion],
    enabled: promptVersion.length > 0,
    staleTime: Infinity,
    queryFn: () =>
      authenticatedApi<{ data: AnalysisCatalog }>(
        getToken,
        `/api/analysis-catalog/${encodeURIComponent(promptVersion)}`,
      ),
  });

  if (promptVersion.length === 0 || query.isError) {
    return { catalog: EMPTY_CATALOG };
  }
  return { catalog: query.data?.data ?? null };
}
